import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Container, Group, Paper, Select, Stack, Switch, Text, Title } from '@mantine/core';
import { IconDownload, IconRefresh } from '@tabler/icons-react';
import { MantineReactTable, useMantineReactTable } from 'mantine-react-table';
import { useMedplum } from '@medplum/react';
import dayjs from 'dayjs';
import { alert } from '@/services/alert';
import { downloadAttachmentBlob } from '@/services/attachments';
import { getRateLimitAlert, isMedplumRateLimitError } from '@/utils/medplumRateLimit';
import { DateRangeFilter } from '@/pages/PatientCentral/shared/DateRangeFilter';
import { CohortDateField, COHORT_DATE_FIELD_OPTIONS, fetchCohortPatientIds } from './cohort';
import { exportRowsToCsv, fetchExportRows, isEmployerCoverageRow, PatientExportRow } from './rows';
import { getPatientExportColumns } from './columns';

const DATE_FORMAT = 'YYYY-MM-DD';

/** Default window: the coming two weeks, matching the biweekly ops send. */
function defaultRange(): [Date, Date] {
  return [dayjs().startOf('day').toDate(), dayjs().add(14, 'day').endOf('day').toDate()];
}

export function PatientExportPage(): ReactElement {
  const medplum = useMedplum();
  const [dateField, setDateField] = useState<CohortDateField>('procedure');
  const [range, setRange] = useState<[Date | null, Date | null]>(defaultRange());
  const [rows, setRows] = useState<PatientExportRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [showContactColumns, setShowContactColumns] = useState(true);
  const [excludeEmployerCoverage, setExcludeEmployerCoverage] = useState(false);
  const columns = useMemo(() => getPatientExportColumns(), []);

  const [from, to] = range;

  /**
   * Employer-coverage exclusion runs here rather than as a column filter: the Track column is
   * "Unknown" for most patients (self-pay episodes aren't backfilled), so an inclusive multi-select
   * cannot express "everything except Carrum" — which is the biweekly send's actual requirement.
   */
  const visibleRows = useMemo(
    () => (excludeEmployerCoverage ? rows.filter((row) => !isEmployerCoverageRow(row)) : rows),
    [rows, excludeEmployerCoverage]
  );
  const excludedCount = rows.length - visibleRows.length;

  const load = useCallback(async () => {
    if (!from || !to) {
      return;
    }

    setLoading(true);
    try {
      const patientIds = await fetchCohortPatientIds(medplum, {
        dateField,
        from: dayjs(from).format(DATE_FORMAT),
        to: dayjs(to).format(DATE_FORMAT),
      });
      setRows(await fetchExportRows(medplum, patientIds));
    } catch (error: unknown) {
      setRows([]);
      if (isMedplumRateLimitError(error)) {
        alert.warning({
          ...getRateLimitAlert(
            'The system temporarily limited how fast we can load this export. Wait a few seconds before trying again.'
          ),
        });
        return;
      }
      alert.error({
        title: 'Error loading the export',
        message: 'An error occurred while loading patients for this export. Please try again later.',
      });
    } finally {
      setLoading(false);
    }
  }, [medplum, dateField, from, to]);

  useEffect(() => {
    load();
  }, [load]);

  const table = useMantineReactTable({
    columns,
    data: visibleRows,
    initialState: { density: 'xs' as const, showColumnFilters: true },
    state: {
      isLoading: loading,
      columnVisibility: { email: showContactColumns, mobile: showContactColumns },
    },
    enableColumnFilters: true,
    enableColumnFilterModes: true,
    enableSorting: true,
    enableGlobalFilter: true,
    enablePagination: false,
    enableRowVirtualization: true,
    enableRowSelection: false,
    enableColumnActions: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableRowActions: false,
    enableTopToolbar: true,
    enableBottomToolbar: false,
    enableToolbarInternalActions: false,
    mantineTableProps: { highlightOnHover: true },
    mantinePaperProps: {
      shadow: 'sm',
      withBorder: true,
      style: { height: '100%', display: 'flex', flexDirection: 'column' },
    },
    mantineTableHeadCellProps: { style: { fontWeight: '500' } },
  });

  /**
   * Serializes the table's filtered + sorted rows — the whole cohort, not a loaded page. `load()`
   * fetches the cohort up front, so nothing here depends on how far the user has scrolled.
   */
  const handleDownloadCsv = (): void => {
    const filteredRows = table.getPrePaginationRowModel().rows.map((row) => row.original);
    const csv = exportRowsToCsv(filteredRows);
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    downloadAttachmentBlob(url, `patient-export-${dayjs().format(DATE_FORMAT)}.csv`);
  };

  const filteredCount = table.getPrePaginationRowModel().rows.length;

  return (
    <Container size="xl" py="md" style={{ height: 'calc(100vh - 60px)', display: 'flex', flexDirection: 'column' }}>
      <Stack gap="lg" style={{ flex: 1, overflow: 'hidden' }}>
        <Group justify="space-between" align="center">
          <Title order={1}>Patient export</Title>
          <Group>
            <Switch
              label="Contact details"
              checked={showContactColumns}
              onChange={(event) => setShowContactColumns(event.currentTarget.checked)}
            />
            <Button variant="outline" leftSection={<IconRefresh size={16} />} onClick={() => load()} loading={loading}>
              Refresh
            </Button>
            <Button leftSection={<IconDownload size={16} />} onClick={handleDownloadCsv} disabled={filteredCount === 0}>
              Download CSV
            </Button>
          </Group>
        </Group>

        <Group align="flex-end" gap="md">
          <Select
            label="Date field"
            data={COHORT_DATE_FIELD_OPTIONS}
            value={dateField}
            allowDeselect={false}
            onChange={(value) => value && setDateField(value as CohortDateField)}
            w={200}
          />
          <DateRangeFilter value={range} onChange={setRange} />
          <Switch
            label="Exclude employer coverage"
            checked={excludeEmployerCoverage}
            onChange={(event) => setExcludeEmployerCoverage(event.currentTarget.checked)}
          />
          <Text size="sm" c="dimmed">
            {filteredCount} of {rows.length} patients
            {excludedCount > 0 ? ` · ${excludedCount} employer-coverage excluded` : ''}
          </Text>
        </Group>

        <Paper style={{ flex: 1, overflow: 'hidden' }}>
          <MantineReactTable table={table} />
        </Paper>
      </Stack>
    </Container>
  );
}
